import React from 'react';
import { cn } from '@/utils';
import { Icon } from '@/components';
import { userStatsVariants } from './UserStats.styles';

export interface UserStatsData {
  completedSimulations?: number;
  averageScore?: number;
  totalTime?: string;
}

export interface UserStatsProps extends React.HTMLAttributes<HTMLDivElement> {
  /**
   * Optional stats data when available
   */
  stats?: UserStatsData;

  /**
   * Optional additional class names for styling
   */
  className?: string;
}

/**
 * A component to display user performance statistics. Shows a placeholder
 * message when no stats are available, or displays completed simulations,
 * average score, and total time when data is available.
 */
export const UserStats: React.FC<UserStatsProps> = ({ 
  className, 
  stats,
  ...props 
}) => {
  const hasValidStats = stats && (stats.completedSimulations !== undefined || stats.averageScore !== undefined || stats.totalTime !== undefined);
  
  return (
    <div className={cn(userStatsVariants(), className)} {...props}>
      <h3 className="font-display text-section-subtitle font-bold leading-section-subtitle text-semantic-text-primary">
        Quick Stats
      </h3>
      <div className="flex items-center gap-2 rounded-lg bg-neutral-white p-3">
        {!hasValidStats ? (
          <>
            <div className="h-8 w-8 flex-shrink-0">
              <Icon name="Lock" size={32} className="text-neutral-gray-500" />
            </div>
            <p className="font-text text-body-small leading-body-small text-semantic-text-secondary">
              Complete your first simulation to unlock stats!
            </p>
          </>
        ) : (
          <div className="flex w-full flex-col gap-1">
            <div className="flex items-center justify-between">
              <span className="font-text text-body-small text-semantic-text-secondary">
                Simulations
              </span>
              <span className="text-body font-text font-medium text-semantic-text-primary">
                {stats?.completedSimulations || 0}
              </span>
            </div>
            {stats?.averageScore && (
              <div className="flex items-center justify-between">
                <span className="font-text text-body-small text-semantic-text-secondary">
                  Average Score
                </span>
                <span className="text-body font-text font-medium text-semantic-text-primary">
                  {stats.averageScore}%
                </span>
              </div>
            )}
            {stats?.totalTime && (
              <div className="flex items-center justify-between">
                <span className="font-text text-body-small text-semantic-text-secondary">
                  Total Time
                </span>
                <span className="text-body font-text font-medium text-semantic-text-primary">
                  {stats.totalTime}
                </span>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

UserStats.displayName = 'UserStats';

export default UserStats;